// OWNED BY the signatures implementation agent (see ../SignaturesTab.tsx ownership note).
//
// Data layer of the customer-page signatures tab (חתימות). Ports the legacy
// per-contact signatures logic (ContactTableNestedRowSignatures + the
// DocumentsList/DocumentDetailPanel live-status pattern) into a lean,
// contact-scoped, cache-backed module:
//
// - Document list = REST `listDocuments({ contactId: contact.id })`
//   (server-side Firestore array-contains on `contactIds`, archived filtered
//   server-side). The query identity is contact.id — the same value the send
//   flow freezes into `contactIds` (NewSignatureForm createDocument) and the
//   same identity the documents tab uses on the SHARED cache key. NEVER query
//   by callerPhoneNumber: for legacy contacts it can be a local-format string
//   that differs from contact.id (sharedSchema note), which would return a
//   different result set and clobber the shared key with wrong data.
// - Live updates = a client Firestore onSnapshot on `signature-documents`
//   where ownerUserId == user email AND contactIds array-contains contact.id.
//   No orderBy in the query (we sort client-side like the legacy list); the
//   composite index (contactIds CONTAINS, ownerUserId ASC) is declared in
//   this repo's firestore.indexes.json so the shape is guaranteed served —
//   we don't rely on single-field index merging. Security rules already allow
//   ownerUserId == email reads (the same contract `subscribeToDocuments` in
//   services/signatures.ts relies on).
// - Timestamp equalization: REST returns ISO strings; onSnapshot (through
//   GenericConverter) returns JS Dates. `deepDatesToIso` (a port of
//   normalizeFirestoreTimestamps from services/signatures.ts, which is not
//   exported) makes both paths store the same ISO-string shape under the
//   SHARED cache key `signatures:{contact.id}` (also read by the documents
//   tab — keep the value type SignatureDocument[]).

import { db } from "firebaseConfig";
import { GenericConverter } from "utils/firestoreConverters";
import {
  archiveDocument,
  displayDocumentStatus,
  listDocuments,
  type SignatureDocumentDisplayStatus
} from "services/signatures";
import { signatureDocumentDisplayName } from "utils/signatureQuestionnaireName";
import { sendSigningLinkFor } from "services/signingLinkDelivery";
import type { SignatureQuickPickRole } from "components/_dashboard/signatures/NewSignatureForm";
import type { LabelColor } from "components/Label";
import { fetchCached, getCached, setCached } from "../../data/sessionCache";
import type {
  SignatureDocument,
  SignatureDocumentSigner,
  SignatureDocumentStatus,
  SignatureSignerStatus
} from "../../../../../../@types/signatures";
import type { RealEstateContact } from "../../../../../../@types/sharedSchema";
import {
  SIGNATURE_DOCUMENTS_COLLECTION_NAME,
  isQuestionnaireOnlyDocument
} from "../../../../../../@types/sharedSchema";

// ---------------------------------------------------------------------------
// Cache keys (shared-key convention: "<tabId>:<contact.id>")
// ---------------------------------------------------------------------------

/** SHARED key (signatures + documents tabs) — value type is SignatureDocument[]. */
export const signaturesCacheKey = (contactId: string): string => `signatures:${contactId}`;

export const SIGNATURES_MAX_AGE_MS = 60_000;

// ---------------------------------------------------------------------------
// Loading + preload (REST — the legacy nested-row query)
// ---------------------------------------------------------------------------

const sortSignatureDocs = (docs: SignatureDocument[]): SignatureDocument[] =>
  [...docs].sort((a, b) =>
    (b.sentAt || b.createdTime || "").localeCompare(a.sentAt || a.createdTime || "")
  );

// The signatures API is feature-flag gated server-side (403 for users without
// the "signature" flag). preload fires on EVERY contacts-table row hover, so
// after the first 403 we stop re-firing doomed requests for the session.
let signatureFeatureForbidden = false;

export const loadContactSignatures = async (
  contact: RealEstateContact
): Promise<SignatureDocument[]> => {
  const contactId = contact.id;
  if (!contactId) return [];
  try {
    const docs = await listDocuments({ contactId });
    signatureFeatureForbidden = false;
    return sortSignatureDocs(docs);
  } catch (err: any) {
    if (err?.response?.status === 403) signatureFeatureForbidden = true;
    throw err;
  }
};

/** Fire-and-forget warm-up — idempotent-cheap (fetchCached dedup + maxAge). */
export const preloadContactSignatures = (contact: RealEstateContact): void => {
  if (!contact?.id || signatureFeatureForbidden) return;
  void fetchCached(
    signaturesCacheKey(contact.id),
    () => loadContactSignatures(contact),
    { maxAgeMs: SIGNATURES_MAX_AGE_MS }
  ).catch(() => undefined);
};

// ---------------------------------------------------------------------------
// Live updates (client onSnapshot → same shared cache key)
// ---------------------------------------------------------------------------

/**
 * Deep Date/Timestamp → ISO-string normalizer. Port of the (unexported)
 * normalizeFirestoreTimestamps in services/signatures.ts so the onSnapshot
 * path stores the exact same value shape as the REST path.
 */
const deepDatesToIso = <T>(value: T): T => {
  if (value === null || value === undefined) return value;
  if (value instanceof Date) return value.toISOString() as unknown as T;
  if (typeof (value as any)?.toDate === "function") {
    try {
      return (value as any).toDate().toISOString() as unknown as T;
    } catch {
      return value;
    }
  }
  if (Array.isArray(value)) return value.map((v) => deepDatesToIso(v)) as unknown as T;
  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = deepDatesToIso(v);
    }
    return out as T;
  }
  return value;
};

/**
 * Realtime per-contact subscription. Writes every snapshot into the shared
 * session-cache key, so the tab body AND the tab-strip badge update live
 * (signer status flips, WhatsApp delivery, completion) without polling.
 * Returns the unsubscribe function. On listener failure (rules/index edge
 * cases) it degrades gracefully: logs + one REST revalidation, cache intact.
 */
export const subscribeContactSignaturesLive = (
  contact: RealEstateContact,
  ownerEmail: string
): (() => void) => {
  const contactId = contact.id;
  const key = signaturesCacheKey(contactId);
  if (!contactId || !ownerEmail) return () => undefined;

  const query = db
    .collection(SIGNATURE_DOCUMENTS_COLLECTION_NAME)
    // Same `any` cast as services/signatures.ts subscribeToDocuments — the
    // GenericConverter constraint expects createdTime: Date while the client
    // SignatureDocument types dates as ISO strings; deepDatesToIso converts.
    .withConverter(GenericConverter<any>() as any)
    .where("ownerUserId", "==", ownerEmail)
    // contact.id — the identity the send flow writes into contactIds; see the
    // module header. Composite index declared in firestore.indexes.json.
    .where("contactIds", "array-contains", contactId);

  return query.onSnapshot(
    (snapshot: any) => {
      const docs: SignatureDocument[] = snapshot.docs
        .map((d: any) => deepDatesToIso(d.data()) as SignatureDocument)
        .filter((d: SignatureDocument) => !d.archivedAt);
      setCached(key, sortSignatureDocs(docs));
    },
    (err: any) => {
      // eslint-disable-next-line no-console
      console.error("subscribeContactSignaturesLive failed:", err);
      // Listener is dead after an error — refresh once via REST so the tab
      // still shows correct (if non-live) data.
      void fetchCached(key, () => loadContactSignatures(contact), { maxAgeMs: 0 }).catch(
        () => undefined
      );
    }
  );
};

// ---------------------------------------------------------------------------
// Cache mutation helpers (optimistic merges after REST actions)
// ---------------------------------------------------------------------------

const applyDocumentToCache = (contactId: string, updated: SignatureDocument): void => {
  const key = signaturesCacheKey(contactId);
  const current = getCached<SignatureDocument[]>(key);
  if (!current) return;
  const next = current.some((d) => d.id === updated.id)
    ? current.map((d) => (d.id === updated.id ? updated : d))
    : sortSignatureDocs([updated, ...current]);
  setCached(key, next);
};

const removeDocumentFromCache = (contactId: string, documentId: string): void => {
  const key = signaturesCacheKey(contactId);
  const current = getCached<SignatureDocument[]>(key);
  if (!current) return;
  setCached(
    key,
    current.filter((d) => d.id !== documentId)
  );
};

/**
 * The per-signer reminder ("שלח תזכורת"): WhatsApp + SMS. A contract of a set sends its whole set as
 * ONE message (signingLinkDelivery.ts); every contract it covers is refreshed in the cache.
 */
export const resendSignatureReminder = async (
  contactId: string,
  doc: Pick<SignatureDocument, "id" | "bundleId">,
  signerId: string
): Promise<{ updated: SignatureDocument; covered: number }> => {
  const { documents, covered } = await sendSigningLinkFor(doc, signerId);
  documents.forEach((updated) => applyDocumentToCache(contactId, updated));
  return { updated: documents.find((d) => d.id === doc.id) ?? documents[0], covered };
};

export const archiveContactSignatureDocument = async (
  contactId: string,
  documentId: string
): Promise<void> => {
  await archiveDocument(documentId);
  removeDocumentFromCache(contactId, documentId);
};

// ---------------------------------------------------------------------------
// Grouping + derived display data
// ---------------------------------------------------------------------------

export type SignatureGroups = {
  /** sent + partial — awaiting at least one signature. */
  pending: SignatureDocument[];
  drafts: SignatureDocument[];
  completed: SignatureDocument[];
  /** declined + expired. */
  closed: SignatureDocument[];
};

export const groupContactSignatures = (docs: SignatureDocument[]): SignatureGroups => {
  // Defensive sort at read time: the SHARED `signatures:{contactId}` key is
  // also written by the documents tab's fetcher, which stores the RAW
  // listDocuments response (no server orderBy). Sorting here keeps row order
  // stable regardless of which writer last populated the key.
  const sorted = sortSignatureDocs(docs);
  return {
    pending: sorted.filter((d) => d.status === "sent" || d.status === "partial"),
    drafts: sorted.filter((d) => d.status === "draft"),
    completed: sorted.filter((d) => d.status === "completed"),
    closed: sorted.filter((d) => d.status === "declined" || d.status === "expired")
  };
};

/** Badge source: documents still awaiting signature (sent/partial). */
export const countPendingSignatures = (docs: SignatureDocument[] | undefined): number =>
  docs?.filter((d) => d.status === "sent" || d.status === "partial").length ?? 0;

export const signersOf = (doc: SignatureDocument): SignatureDocumentSigner[] =>
  doc.signers ?? [];

/** "חתימות X/Y" — signed signers out of total. */
export const signerProgress = (doc: SignatureDocument): { signed: number; total: number } => {
  const signers = signersOf(doc);
  return {
    signed: signers.filter((s) => s.status === "signed").length,
    total: signers.length
  };
};

/**
 * How many times the document was opened by signers. Native docs carry an
 * append-only auditEvents trail per signer ('view' per open); legacy docs
 * only have viewedAt — fall back to counting signers who viewed at all.
 */
export const documentOpenCount = (doc: SignatureDocument): number => {
  const signers = signersOf(doc);
  const views = signers.reduce(
    (acc, s) => acc + (s.auditEvents?.filter((e) => e.type === "view").length ?? 0),
    0
  );
  if (views > 0) return views;
  return signers.filter((s) => s.viewedAt).length;
};

export const wasSentViaWhatsapp = (doc: SignatureDocument): boolean =>
  signersOf(doc).some((s) => s.whatsappDelivery?.status === "sent");

/** Signers a reminder can still be sent to (has a signing slug, not signed/declined). */
export const remindableSigners = (doc: SignatureDocument): SignatureDocumentSigner[] =>
  signersOf(doc).filter(
    (s) => s.status !== "signed" && s.status !== "declined" && !!s.signing?.slug
  );

// ---------------------------------------------------------------------------
// Hebrew labels + Label colors (ported from the legacy status mappings)
// ---------------------------------------------------------------------------

export const documentStatusLabelHe: Record<SignatureDocumentDisplayStatus, string> = {
  draft: "טיוטה",
  created: "נוצר - לא נשלח מ-Webot",
  sent: "ממתין לחתימה",
  partial: "נחתם חלקית",
  completed: "הושלם",
  declined: "נדחה",
  expired: "פג תוקף"
};

/**
 * A questionnaire-only document (שאלון בלבד) is the questionnaire itself: the owner FILLS it in and
 * signs it, so it is "waiting to be filled", and it was "filled and signed". Every other state, and
 * every contract, reads as above.
 */
export const isQuestionnaireDocument = (doc: SignatureDocument): boolean => isQuestionnaireOnlyDocument(doc);

export const documentStatusLabelFor = (doc: SignatureDocument): string => {
  const status = displayDocumentStatus(doc);
  return isQuestionnaireOnlyDocument(doc) && status === "sent" ? "ממתין למילוי" : documentStatusLabelHe[status];
};

/** The row's name: the template's, or a questionnaire-only document's own ("שאלון מוכר"). */
export const documentTitleOf = (doc: SignatureDocument): string => signatureDocumentDisplayName(doc);

// Mock s25 shows pending in warning; the rest ports DocumentDetailPanel's map.
export const documentStatusColor: Record<SignatureDocumentDisplayStatus, LabelColor> = {
  draft: "default",
  created: "default",
  sent: "warning",
  partial: "warning",
  completed: "success",
  declined: "error",
  expired: "default"
};

export const signerStatusLabelHe: Record<SignatureSignerStatus, string> = {
  pending: "ממתין",
  sent: "נשלח",
  viewed: "נצפה",
  signed: "חתם",
  declined: "דחה"
};

export const signerStatusColor: Record<SignatureSignerStatus, LabelColor> = {
  pending: "default",
  sent: "primary",
  viewed: "info",
  signed: "success",
  declined: "error"
};

/** Compact Hebrew relative day: "היום 10:05" / "אתמול" / "לפני יומיים" / date. */
export const relativeDayLabel = (value?: string): string => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  const startOfDay = (x: Date): number =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86_400_000);
  if (diffDays <= 0) {
    const time = d.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" });
    return `היום ${time}`;
  }
  if (diffDays === 1) return "אתמול";
  if (diffDays === 2) return "לפני יומיים";
  if (diffDays <= 7) return `לפני ${diffDays} ימים`;
  return d.toLocaleDateString("he-IL");
};

export const shortDateHe = (value?: string): string => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("he-IL", { day: "numeric", month: "numeric" });
};

// ---------------------------------------------------------------------------
// Seller-side listing prefill for the send flow
// ---------------------------------------------------------------------------

/**
 * The listing id to prefill into the new-signature flow for this contact,
 * given the signing role derived from its customer types: a מוכר signs over
 * their `sellingListingIds`, a משכיר over their `rentingOutListingIds` —
 * first (usually only) id of the role-matched list, per Haim. Buyer-side
 * roles (and contacts with no listing on file) get undefined — no prefill.
 * The id feeds `buildNewSignatureParams({ listingId })`; the send form
 * resolves + applies it only when the picked template consumes listings.
 */
export const contactPrefillListingIdForRole = (
  contact: RealEstateContact,
  role: SignatureQuickPickRole | undefined
): string | undefined => {
  const ids =
    role === "seller"
      ? contact.sellingListingIds
      : role === "landlord"
        ? contact.rentingOutListingIds
        : undefined;
  return ids?.find((id) => Boolean(id && id.trim())) || undefined;
};
